import {DISCOUNT_OPTIONS, FINANCING_MODES, INSURANCE_LABELS_BY_ID_NAME} from './config.js'

// Renders as `120 €/mes*`. The asterisk points at the legal conditions link below the panel.
export const formatMonthlyFee = ({fee, i18n}) => `${i18n.c(fee)}/${i18n.t('COMMON.PLURALS.MONTH', 1).toLowerCase()}*`

// Negative amounts are allowed here. The promotional discount is a negative number, and its minus
// symbol comes from this formatter. Returning null hides the row, so never use it to hide one on
// purpose — that is what each row's own `isVisible` is for.
const formatMoney = ({i18n, value}) => (value == null || isNaN(value) ? null : i18n.c(value))

const formatPercentage = ({i18n, value}) => (value == null || isNaN(value) ? null : i18n.f('percentage', value))

/**
 * Returns the financing flavour this ad qualifies for, or `null` when the panel should not render.
 *
 * `showB2cFinancingPanel` gets a check of its own because it is derived from `seller.isDealer`,
 * while the dealer-offer check reads `seller.dealer`. Those two do not always agree.
 */
export const resolveFinancingMode = ({dealerPacks, financingData, seller, showB2cFinancingPanel, showC2cFinancing}) => {
  const isDealerSubscription = Boolean(seller.isDealer) && seller.dealer?.pack === dealerPacks.SUBSCRIPTION
  if (isDealerSubscription) return null

  const hasActiveDealerFinancingOffer = Boolean(seller.dealer) && Boolean(financingData?.id)
  if (!hasActiveDealerFinancingOffer && !showC2cFinancing && !showB2cFinancingPanel) return null

  if (showC2cFinancing) return FINANCING_MODES.C2C
  if (showB2cFinancingPanel) return FINANCING_MODES.B2C
  return FINANCING_MODES.SIMPLIFIED
}

/**
 * Which help text sits under the entry input, or `null` for none. Ported from `getInputHelpText` in
 * `@adv-mt/mt-panel-financing`: the text only ever talks about the promotional discount, so there is
 * nothing to say once the discount is off the table. Its min/max branch is deliberately left out —
 * legacy never returns it, so it has never rendered.
 */
export const getEntryHelpKey = ({cashPrice, entry, financedPrice, maxEntryToGetDiscount, selectedOption}) => {
  // Every one of these texts is about what an entry does to the discount, so with no entry yet there
  // is nothing to warn about — a zero entry is the case the discount applies to in full.
  if (!Number(entry)) return null
  if (cashPrice === financedPrice) return null
  if (selectedOption === DISCOUNT_OPTIONS.WITHOUT) return 'PANEL_FINANCING.REVAMP.B2C.INPUT_HELP_TEXT_WITHOUT_DISCOUNT'
  if (selectedOption !== DISCOUNT_OPTIONS.WITH) return null

  return Number(maxEntryToGetDiscount) < Number(entry)
    ? 'PANEL_FINANCING.B2C.INPUT_HELP_TEXT_WITH_DISCOUNT_MAX_ENTRY_EXCEEDED'
    : 'PANEL_FINANCING.B2C.INPUT_HELP_TEXT_WITH_DISCOUNT_MAX_ENTRY_NOT_EXCEEDED'
}

/**
 * The entry the lender is willing to quote is a closed range. Returns the literal key and the amount
 * to interpolate, or `null` when the value is quotable.
 */
export const getEntryError = ({maxEntry, minEntry, value}) => {
  const entry = Number(value)
  if (minEntry != null && entry < minEntry) return {amount: minEntry, key: 'ENTRY_MINIMUM_VALUE'}
  if (maxEntry != null && entry > maxEntry) return {amount: maxEntry, key: 'ENTRY_MAXIMUM_VALUE'}
  return null
}

// Takes the lender's `insuranceId` and finds the name it is declared under in the tenant's config,
// because that name is how INSURANCE_LABELS_BY_ID_NAME is keyed. Ids are compared as strings
// because the lender sometimes sends a number and sometimes a string.
const resolveInsuranceIdName = ({insuranceId, insuranceIds = {}}) =>
  insuranceId == null
    ? undefined
    : Object.keys(insuranceIds).find(name => String(insuranceIds[name]) === String(insuranceId))

/**
 * Builds the financing breakdown table. Every row is ported from `useGetListOfFinancingConditions`
 * in `@adv-mt/mt-table-financing-details`, which is what the legacy detail renders.
 *
 * The rows stay as one flat list in the same order as legacy, each with its own `isVisible`, so
 * that both versions can be read side by side whenever a rule changes.
 *
 * Pass the settled quote (`appliedFinancingData` in `index.js`), never the raw ad data. Every
 * figure has to come from the same quote, or the table ends up showing one quote's fee next to
 * another quote's capital.
 *
 * Legacy also has an `instalment` row. It only shows inside a modal, and this table is not in one.
 */
export const getDetailRows = ({financingData, financingPartners = {}, i18n, insuranceIds}) => {
  const {
    amountToFinance,
    capital,
    cashPrice,
    computedPrice,
    entry,
    financedPrice,
    firstFee,
    insuranceId,
    lender,
    licenseWithdrawalInsuranceAmount,
    lifeInsuranceAmount,
    loanRequestAvailable = false,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalLoanCost,
    totalTermsAmount,
    vehiclePrice
  } = financingData

  const money = value => formatMoney({i18n, value})
  const percentage = value => formatPercentage({i18n, value})

  // The price the loan is calculated on. When it matches the cash price there is no promotion.
  const financedPriceFda = computedPrice || financedPrice || cashPrice
  const hasNotDiscount = financedPriceFda === cashPrice
  const promotionalDiscount = financedPriceFda - cashPrice

  const isFdaCalculatorB2cAvailable = loanRequestAvailable
  // The `lender` check comes first on purpose. Motos declares fewer partners than coches, so a
  // partner missing from the config would compare `undefined === undefined` and switch every
  // lender gate on at once.
  const isLender = partner => Boolean(lender) && partner === lender
  const isBmwBankLender = isLender(financingPartners.BMW_BANK)
  const isLaCaixaLender = isLender(financingPartners.CAIXA)
  const isSantanderPriLender = isLender(financingPartners.SANTANDER_PRI)
  const isConfiaLender = isLender(financingPartners.CONFIA)
  const isVWFSLender = isLender(financingPartners.VWFS)

  // A zero means the lender did not quote this concept, so we drop the row instead of showing `0 €`.
  const isQuoted = amount => Boolean(amount) && amount >= 0

  const insurance = INSURANCE_LABELS_BY_ID_NAME[resolveInsuranceIdName({insuranceId, insuranceIds})]
  const insuranceAmount = insurance?.usesTotalInsuranceAmount ? totalInsuranceAmount : lifeInsuranceAmount

  // Confia finances its opening fee instead of charging it up front, so it needs its own wording.
  // Legacy checks Confia's two insurance ids to get here. Checking the lender instead keeps working
  // if Confia ever adds a third one.
  const openingFeeLabelKey = isConfiaLender ? 'FINANCING_INFO.CONFIA_INFO.OPENING_FEE' : 'FINANCING_INFO.OPENING_FEE'

  const rows = [
    {
      id: 'vehiclePrice',
      isVisible: !isSantanderPriLender && isQuoted(vehiclePrice),
      label: i18n.t('FINANCING_INFO.TOTAL_PRICE_VEHICLE'),
      value: money(vehiclePrice)
    },
    {
      id: 'cashPrice',
      isVisible: isFdaCalculatorB2cAvailable && isQuoted(cashPrice) && !hasNotDiscount,
      label: i18n.t('FINANCING_INFO.CASH_PRICE'),
      value: money(cashPrice)
    },
    {
      // The only row with a negative amount. Once the entry goes past the discount ceiling, or the
      // financed price overtakes the cash price, the discount shows as zero instead of disappearing.
      id: 'promotionalDiscount',
      isVisible: isFdaCalculatorB2cAvailable && !hasNotDiscount && promotionalDiscount <= 0,
      label: i18n.t('FINANCING_INFO.PROMOTIONAL_DISCOUNT'),
      value: money(
        (entry !== undefined && entry > maxEntryToGetDiscount) || cashPrice < financedPriceFda ? 0 : promotionalDiscount
      )
    },
    {
      id: 'computedPrice',
      isVisible: !isSantanderPriLender && isQuoted(financedPriceFda),
      label: i18n.t('FINANCING_INFO.FINANCED_PRICE'),
      value: money(financedPriceFda)
    },
    {
      id: 'capital',
      isVisible: isSantanderPriLender && isQuoted(capital),
      label: i18n.t('FINANCING_INFO.REQUESTED_AMOUNT'),
      value: money(capital)
    },
    {
      // Hidden for the personal loan, where the cash payment is the calculator's own input above.
      id: 'minEntry',
      isVisible: !isSantanderPriLender && minEntry !== undefined && minEntry !== null,
      label: i18n.t('FINANCING_INFO.ENTRANCE_FEE'),
      value: money(entry !== undefined ? entry : minEntry)
    },
    {
      id: 'terms',
      isVisible: Boolean(terms) && !isSantanderPriLender,
      label: i18n.t('COMMON.PLURALS.MONTH', 2),
      value: terms == null ? null : String(terms)
    },
    {
      id: 'financingAmount',
      isVisible: isBmwBankLender && isQuoted(amountToFinance),
      label: i18n.t('FINANCING_INFO.FINANCED_AMOUNT'),
      value: money(amountToFinance)
    },
    {
      id: 'totalPriceTerms',
      isVisible: isBmwBankLender && Boolean(totalAmountDue),
      label: i18n.t('FINANCING_INFO.TOTAL_PRICE_TERMS'),
      value: money(totalAmountDue)
    },
    {
      extraLabel: insurance?.extraLabel ? i18n.t(insurance.extraLabel) : undefined,
      id: 'lifeInsuranceAmount',
      // For CaixaBank this amount has a `**` footnote in the legal conditions below the panel.
      isHighlighted: isLaCaixaLender,
      isVisible: Boolean(insurance?.label) && Boolean(insuranceAmount),
      label: insurance?.label ? i18n.t(insurance.label) : '',
      value: money(insuranceAmount)
    },
    {
      extraLabel: `(${i18n.t('FINANCING_INFO.OPTIONAL')})`,
      id: 'licenseWithdrawalInsuranceAmount',
      isVisible: isVWFSLender && Boolean(licenseWithdrawalInsuranceAmount),
      label: i18n.t('FINANCING_INFO.WITHDRAWAL_INSURANCE'),
      value: money(licenseWithdrawalInsuranceAmount)
    },
    {
      id: 'totalLoanCost',
      isVisible: isVWFSLender && Boolean(totalLoanCost),
      label: i18n.t('FINANCING_INFO.TOTAL_LOAN_AMOUNT'),
      value: money(totalLoanCost)
    },
    {
      id: 'totalAmountTerms',
      isVisible: Boolean(totalTermsAmount) && !isSantanderPriLender && !isBmwBankLender,
      label: i18n.t('FINANCING_INFO.TOTAL_AMOUNT_TERMS'),
      value: money(totalTermsAmount)
    },
    {
      id: 'openingExpenses',
      isVisible: Boolean(openingExpenses),
      label: i18n.t(openingFeeLabelKey),
      value: money(openingExpenses)
    },
    {
      id: 'tin',
      isVisible: Boolean(tin),
      label: i18n.t('FINANCING_INFO.TIN'),
      value: percentage(tin)
    },
    {
      id: 'tae',
      isBold: true,
      isVisible: Boolean(tae),
      label: i18n.t('FINANCING_INFO.TAE'),
      value: percentage(tae)
    },
    {
      id: 'totalAmountDue',
      isVisible: isSantanderPriLender && Boolean(totalAmountDue),
      label: i18n.t('FINANCING_INFO.TOTAL_AMOUNT_DUE'),
      value: money(totalAmountDue)
    },
    {
      id: 'firstInstallment',
      isVisible: !isSantanderPriLender && !isConfiaLender && Boolean(firstFee),
      label: i18n.t('FINANCING_INFO.FIRST_INSTALLMENT'),
      value: money(firstFee)
    }
  ]

  return rows.filter(({isVisible, value}) => isVisible && value != null)
}
